/**
 * events/runStep.ts — драйвер шага по псевдокоду tech/21 §5 (D14/R1 — ФУНДАМЕНТ).
 *
 * Модель исполнения:
 *  1) верхний уровень (body) ИМПЕРАТИВНО зовёт ops → каждая операция немедленно
 *     мутирует черновик (через reducer) и кладёт событие в FIFO-очередь;
 *  2) ОТЛОЖЕННЫЙ дренаж: на каждое снятое событие — подписчики в каноническом
 *     порядке (tech/21 §9); их ops кладут НОВЫЕ события в очередь; цикл до
 *     опустошения. Реентрантности нет: пока хук выполняется, чужие хуки не
 *     запускаются — межхуковые реакции идут только через очередь.
 *
 * Граница чистоты: вход runStep НЕ мутируется — черновик копируется один раз
 * (structuredClone), см. events/reducer.ts (форма мутации зафиксирована там).
 *
 * ЛИМИТ СОБЫТИЙ (предохранитель от «безумной» карты, tech/21 §5): не более
 * EVENT_LIMIT_PER_STEP обработанных событий на один шаг. Превышение ПОМЕЧАЕТ
 * МАТЧ ОШИБОЧНЫМ. Форма пометки (зафиксирована в M2): runStep НЕ бросает и не
 * виснет — возвращает { status: 'match_error', reason }, дренаж прерывается.
 * GameState не расширяется полем ошибки (форма состояния — строго tech/21 §2);
 * решение «что делать с ошибочным матчем» принимает вызывающий слой (M5).
 */
import { HookRegistry } from '../hooks/hooks.js';
import { bindOps } from '../ops/ops.js';
import { EventQueue } from './queue.js';
import { applyEvent } from './reducer.js';
/** Лимит обработанных событий на один шаг (Action) — tech/21 §5. */
export const EVENT_LIMIT_PER_STEP = 10_000;
/**
 * ОБЩИЙ дренаж очереди (tech/21 §5): снимает события FIFO, дописывает их в
 * `events` и на каждое зовёт `dispatch` (диспетчеризация хуков — форма источника
 * подписок задаётся вызывающим: тест-шов HookRegistry в M2 либо реестр карт в
 * M3a). Предохранитель лимита — здесь, единая точка для обоих драйверов.
 * Мутирует переданный массив `events` (накапливает обработанные).
 */
export function drainQueue(queue, events, dispatch) {
    while (!queue.isEmpty()) {
        if (events.length >= EVENT_LIMIT_PER_STEP) {
            return {
                ok: false,
                reason: `превышен лимит событий на шаг (${EVENT_LIMIT_PER_STEP}) — вероятно, бесконечная цепочка хуков; ` +
                    'матч помечается ошибочным (tech/21 §5)',
            };
        }
        const event = queue.dequeue();
        events.push(event);
        dispatch(event);
    }
    return { ok: true };
}
/**
 * Пост-дренажная финализация смертей (D25): событием TokenRemoved удаляет
 * ТОКЕНЫ, для которых в этом шаге прошло событие UnitDied. Вызывается ПОСЛЕ
 * полного дренажа — так onDeath
 * токена успевает сработать с живым ctx.self (иначе токен исчез бы до триггера,
 * баг M3a-ревью M1). Обычные карты уходят в discard парным CardMoved (destroy),
 * поэтому здесь не трогаются (isToken !== true). TokenRemoved не запускает
 * новые хуки: это финальный reducer-only факт после завершения реакций смерти.
 * Детерминировано: порядок первых UnitDied в журнале.
 */
export function finalizeDeadUnits(draft, events) {
    const deaths = events.filter((event) => event.type === 'UnitDied');
    const finalized = new Set();
    const removalEvents = [];
    for (const ev of deaths) {
        if (finalized.has(ev.instanceId))
            continue;
        if (ev.type !== 'UnitDied')
            continue;
        const inst = draft.instances[ev.instanceId];
        if (inst?.isToken === true) {
            const event = {
                type: 'TokenRemoved',
                instanceId: inst.id,
                defId: inst.defId,
                ownerId: inst.ownerId,
            };
            applyEvent(draft, event);
            removalEvents.push(event);
            finalized.add(ev.instanceId);
        }
    }
    const matchEndedIndex = events.findIndex((event) => event.type === 'MatchEnded');
    if (matchEndedIndex < 0)
        events.push(...removalEvents);
    else
        events.splice(matchEndedIndex, 0, ...removalEvents);
}
/**
 * Выполнить один шаг: body императивно зовёт ops, затем дренаж очереди
 * с диспетчеризацией хуков (hooks; по умолчанию — пустой реестр).
 */
export function runStep(state, body, hooks = new HookRegistry()) {
    // Единственная точка копирования: дальше все мутации — in-place через reducer.
    const draft = structuredClone(state);
    const queue = new EventQueue();
    const ctx = { state: draft, ops: bindOps(draft, queue) };
    // 1) Верхний уровень: ops немедленны (мутация + событие в очередь).
    body(ctx);
    // 2) Отложенный дренаж очереди (источник подписок — тест-шов HookRegistry).
    const events = [];
    const outcome = drainQueue(queue, events, (event) => {
        for (const sub of hooks.collect(event)) {
            // Хук императивен: его ops мутируют черновик и кладут НОВЫЕ события.
            sub.hook(ctx, event);
        }
    });
    if (!outcome.ok) {
        return { status: 'match_error', state: draft, events, reason: outcome.reason };
    }
    finalizeDeadUnits(draft, events);
    return { status: 'completed', state: draft, events };
}
//# sourceMappingURL=runStep.js.map